import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AnonymousAuthService } from './anonymous-auth.service';
import { API_PATHS } from '../config/api-paths';
import {
  ProspectConsentApi,
  ProspectConsentContext,
  ProspectConsentCaptureRequest,
} from '../tokens/prospect-consent.token';

/**
 * Public Stage-0b prospect consent-capture API (BRS §1A.0c, EM-773, BR-ED-003).
 *
 * <p>Backs the emailed / SMS'd consent link `.../onboarding/consent/{applicationReference}`. Both
 * endpoints are `permitAll` on the embedded service; each call obtains a short-lived ROLE_ANONYMOUS
 * token from {@link AnonymousAuthService} — the prospect SME is not yet signed in. The prospect is
 * resolved server-side from the opaque application reference (Rule 22 — no internal id in the URL).
 *
 * REST-only (Rules 07/10): there is no Mock* counterpart.
 */
@Injectable({ providedIn: 'root' })
export class ProspectConsentRestService implements ProspectConsentApi {
  private readonly http = inject(HttpClient);
  private readonly anonymousAuth = inject(AnonymousAuthService);

  getContext(applicationReference: string): Observable<ProspectConsentContext> {
    const ref = encodeURIComponent(applicationReference);
    return this.anonymousAuth.getToken().pipe(
      switchMap(token =>
        this.http.get<ProspectConsentContext>(
          `${API_PATHS.EMBEDDED_API_BASE}/prospect-consent/${ref}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      ),
    );
  }

  capture(applicationReference: string, request: ProspectConsentCaptureRequest): Observable<void> {
    const ref = encodeURIComponent(applicationReference);
    return this.anonymousAuth.getToken().pipe(
      switchMap(token =>
        this.http.post<void>(
          `${API_PATHS.EMBEDDED_API_BASE}/prospect-consent/${ref}`,
          request,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      ),
    );
  }
}
